import {
  EntryTypeEnum,
  IConfigOfferEntryDto,
  ILoyaltyPromotionInput,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
  LoyaltyPromotionType,
  ProductTypeDto,
} from 'generated/graphql-gateway';
import { ICartEntryAdapter } from 'state/loyalty/hooks/types';
import { isReward } from 'state/loyalty/in-restaurant-redemption/hooks/use-in-restaurant-redemption-cart';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import { logger } from 'utils/logger';

import { ICartEntry, MinimalCartEntryType } from '../types';

export type LookUpContext = {
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>;
  indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
  indexedRewards: IndexedMenu<IRewardEntryDto>;
};

/**
 * Checks if the cart entries have any offers
 * @param cartEntries - The cart entries to check
 * @returns True if the cart entries have any offers, false otherwise
 */
export const hasOffers = (cartEntries: ICartEntry[]) => {
  return cartEntries.some(entry => isOfferCartEntry(entry));
};

export const hasRewards = (cartEntries: ICartEntry[]) => {
  return cartEntries.some(entry => isReward(entry));
};

export const isOfferCartEntry = (cartEntry: ICartEntry) => {
  return (
    cartEntry.type === MinimalCartEntryType.discount ||
    cartEntry.type === MinimalCartEntryType.incentive
  );
};

export const isRewardCartEntry = (cartEntry: ICartEntry) => {
  return cartEntry.type === MinimalCartEntryType.reward;
};

export const isIncentiveOrCombo = (cartEntry: ICartEntry) => {
  return (
    cartEntry.type === MinimalCartEntryType.combo ||
    cartEntry.type === MinimalCartEntryType.incentive
  );
};

export const isItemOrCombo = (cartEntry: ICartEntry) => {
  return (
    cartEntry.type === MinimalCartEntryType.item || cartEntry.type === MinimalCartEntryType.combo
  );
};

/**
 * Determines the minimal cart entry type for a given entry ID by checking
 * various indexed collections in order of priority.
 *
 * Priority order:
 * 1. Donations
 * 2. Extras
 * 3. Rewards
 * 4. Offers (discount vs incentive)
 * 5. Menu entries (by type)
 */
export const getMinimalCartEntryType = (
  entryId: string,
  lookUpContext: LookUpContext
): MinimalCartEntryType => {
  const { indexedRewards, indexedOffers, indexedMenu } = lookUpContext;

  if (indexedRewards.find(entryId)) {
    return MinimalCartEntryType.reward;
  }

  const offerEntry = indexedOffers.find(entryId);
  if (offerEntry) {
    // Check if discountType is 'percentage' or 'amount' for discount, otherwise incentive
    return offerEntry.discountType === 'PERCENTAGE' || offerEntry.discountType === 'AMOUNT'
      ? MinimalCartEntryType.discount
      : MinimalCartEntryType.incentive;
  }

  const menuEntry = indexedMenu.find(entryId);
  return mapEntryTypeToMinimalCartEntryType(menuEntry?.type, menuEntry?.productType);
};

/**
 * Maps menu entry types to their corresponding minimal cart entry types.
 * Defaults to 'item' for unknown or missing types.
 */
const mapEntryTypeToMinimalCartEntryType = (
  entryType?: EntryTypeEnum,
  productType?: readonly ProductTypeDto[]
): MinimalCartEntryType => {
  switch (entryType) {
    case EntryTypeEnum.ITEM:
      if (productType?.includes(ProductTypeDto.NON_FOOD)) {
        // there are multiple items marked as non-food, at this point we are assuming
        // that anything we add to the cart that is non-food is either a donation or an extra
        return productType?.includes(ProductTypeDto.DONATIONS)
          ? MinimalCartEntryType.donation
          : MinimalCartEntryType.extra;
      }
      return MinimalCartEntryType.item;
    case EntryTypeEnum.COMBO:
      return MinimalCartEntryType.combo;
    case EntryTypeEnum.MODIFIER:
      return MinimalCartEntryType.modifier;
    default:
      return MinimalCartEntryType.item;
  }
};

/**
 * recursively maps the cart entries to the loyalty cart entries
 * @param cartEntries - The cart entries to map
 * @returns The loyalty cart entries
 */
export const mapCartEntriesToLoyaltyCartEntries = (
  cartEntry: ICartEntry[],
  incentiveType: LoyaltyPromotionType
): ICartEntryAdapter[] => {
  return cartEntry.map(entry => ({
    sanityId: entry.id,
    lineId: entry.cartId,
    price: entry.price?.amount ?? 0,
    quantity: entry.quantity ?? 1,
    children: mapCartEntriesToLoyaltyCartEntries(entry.children, incentiveType),
  }));
};

export const getLoyaltyAppliedIncentives = ({
  cartEntries,
  incentiveType,
}: {
  cartEntries: ICartEntry[];
  incentiveType: LoyaltyPromotionType;
}): ILoyaltyPromotionInput[] => {
  return cartEntries
    .filter(entry =>
      incentiveType === LoyaltyPromotionType.OFFER
        ? isOfferCartEntry(entry)
        : isRewardCartEntry(entry)
    )
    .map(entry => ({
      id: entry.referenceId, // in this case is the loyaltyEngineId
      lineId: entry.cartId,
      type: incentiveType,
    }));
};

/**
 * Calculates the price of a cart entry's children
 * @param entry - The cart entry to calculate the children price for
 * @returns The price of the children
 */
export const calculateEntryChildrenPrice = (entry: ICartEntry): number => {
  return (entry.children ?? []).reduce((acc, child) => acc + calculateEntryTotalPrice(child), 0);
};

/**
 * Calculates the total price of a cart entry including all nested children
 */
export const calculateEntryTotalPrice = (entry: ICartEntry): number => {
  const entryPrice = (entry.price?.amount ?? 0) * (entry.quantity ?? 1);
  const childrenPrice = (entry.children ?? []).reduce(
    (acc, child) => acc + calculateEntryTotalPrice(child),
    0
  );
  return entryPrice + childrenPrice;
};

/**
 * Calculates the subtotal of a cart entries excluding the donation items
 * as they are not included in the subtotal calculation
 */
export const calculateCartSubTotal = (cartEntries: ICartEntry[]): number => {
  const subTotal = cartEntries.reduce((acc, entry) => acc + calculateEntryTotalPrice(entry), 0);

  if (subTotal < 0) {
    logger.warn({
      message: '[calculateCartSubTotal]: Subtotal is negative',
      context: { cartEntries, subTotal },
    });
    return 0;
  }

  return subTotal;
};

export const countCartItems = (cartEntries: ICartEntry[]) => {
  return cartEntries.reduce((total, cartEntry) => {
    if (
      cartEntry.cartId === MinimalCartEntryType.discount ||
      cartEntry.type === MinimalCartEntryType.donation ||
      cartEntry.type === MinimalCartEntryType.extra
    ) {
      return total;
    }
    return total + cartEntry.quantity;
  }, 0);
};

export const getRewardBenefitId = (cartEntry: ICartEntry): string | undefined => {
  if (cartEntry.type === MinimalCartEntryType.reward) {
    return cartEntry.children?.[0]?.id;
  }
  return undefined;
};
